import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import ReactEcharts from 'echarts-for-react';

import { Card, MeterBar, Section, StatusChip } from './Card';
import {
  BAND_LABEL_MIN_WEEKS,
  COLORS,
  CONFIDENCE_LABELS,
  DECLINE_30D_PCT,
  DECLINE_90D_PCT,
  GAP_COLORS,
  GAP_LABELS,
  TREND_LABELS,
} from './constants';
import { WEEK_MS, getGapBands, toTime } from './weeks';
import { describeChange, formatNumber, formatOrm, hasExerciseData, isNum } from './format';

const TREND_TONE = {
  rising: 'good',
  steady: 'neutral',
  declining: 'caution',
  insufficient: 'neutral',
};

// Only a clear drop is called a decline; small dips read as "Holding steady".
export const getLiftTrend = (exercise) => {
  if (!hasExerciseData(exercise) || exercise.trend === 'insufficient' || !exercise.trend) {
    return 'insufficient';
  }
  if (
    (isNum(exercise.change90dPct) && exercise.change90dPct <= DECLINE_90D_PCT) ||
    (isNum(exercise.change30dPct) && exercise.change30dPct <= DECLINE_30D_PCT)
  ) {
    return 'declining';
  }
  return exercise.trend === 'rising' ? 'rising' : 'steady';
};

// Time axis so missing weeks show as real gaps, with a null point inserted into each gap and
// connectNulls off so the line breaks instead of drawing across weeks without data.
export const buildSparklineOption = (points, weeks) => {
  const valid = (points || [])
    .map((p) => ({ time: toTime(p?.weekStart), orm: p?.orm }))
    .filter((p) => p.time !== null && isNum(p.orm))
    .sort((a, b) => a.time - b.time);

  const data = [];
  valid.forEach((p, index) => {
    const prev = valid[index - 1];
    if (prev && p.time - prev.time > WEEK_MS * 1.5) {
      data.push([prev.time + WEEK_MS, null]);
    }
    data.push([p.time, p.orm]);
  });

  const values = valid.map((p) => p.orm);
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  // Pad the range so tiny week to week noise does not fill the whole chart height.
  const pad = Math.max((hi - lo) * 0.25, Math.abs(hi) * 0.05, 1);

  // Away and break weeks are shaded, clamped to the plotted range. Only bands wide enough to fit
  // text get a label; narrower ones rely on the shading, the aria-label and the weekly legend.
  const axisStart = valid.length ? valid[0].time : null;
  const axisEnd = valid.length ? valid[valid.length - 1].time : null;
  const bands = valid.length
    ? getGapBands(weeks)
        .map((band) => ({
          ...band,
          start: Math.max(band.start, axisStart),
          end: Math.min(band.end, axisEnd),
        }))
        .filter((band) => band.end > band.start)
    : [];
  const markArea = bands.length
    ? {
        silent: true,
        data: bands.map((band) => [
          {
            name: GAP_LABELS[band.status],
            xAxis: band.start,
            itemStyle: { color: GAP_COLORS[band.status] },
            label:
              Math.round((band.end - band.start) / WEEK_MS) >= BAND_LABEL_MIN_WEEKS
                ? {
                    show: true,
                    position: 'insideTop',
                    color: '#fff',
                    fontSize: 9,
                    formatter: GAP_LABELS[band.status],
                  }
                : { show: false },
          },
          { xAxis: band.end },
        ]),
      }
    : undefined;

  return {
    animation: false,
    grid: { left: 2, right: 2, top: 4, bottom: 4 },
    xAxis: { type: 'time', show: false },
    yAxis: { type: 'value', show: false, min: lo - pad, max: hi + pad },
    series: [
      {
        type: 'line',
        data,
        connectNulls: false,
        smooth: false,
        showSymbol: true,
        symbolSize: 3,
        itemStyle: { color: COLORS.accent },
        lineStyle: { color: COLORS.accent, width: 2 },
        areaStyle: { color: COLORS.accent, opacity: 0.12 },
        ...(markArea ? { markArea } : {}),
      },
    ],
  };
};

const Sparkline = ({ points, label, weeks }) => {
  const option = useMemo(() => buildSparklineOption(points, weeks), [points, weeks]);
  const hasBands = Boolean(option.series[0].markArea);
  const valid = (points || []).filter((p) => isNum(p?.orm));
  if (valid.length < 2) {
    return null;
  }
  const first = valid[0];
  const last = valid[valid.length - 1];
  return (
    <Box
      role="img"
      aria-label={`${label} weekly estimated 1RM, ${formatNumber(first.orm)} to ${formatNumber(
        last.orm
      )} pounds from ${first.weekStart} to ${last.weekStart}${
        hasBands ? '. Shaded bands mark weeks you were away or on a break.' : ''
      }`}
    >
      <div aria-hidden="true">
        <ReactEcharts
          option={option}
          opts={{ renderer: 'svg' }}
          style={{ height: 48, width: '100%' }}
        />
      </div>
    </Box>
  );
};

const Change = ({ label, value }) => {
  const { arrow, text, direction } = describeChange(value);
  const spoken =
    direction === 'none'
      ? 'no baseline yet'
      : `${direction === 'flat' ? 'unchanged' : direction} ${text}`;
  return (
    <Box sx={{ fontSize: 14 }}>
      <Box component="span" sx={{ opacity: 0.7 }}>
        {label}{' '}
      </Box>
      <Box component="span" aria-label={`${label} ${spoken}`} sx={{ fontWeight: 600 }}>
        {arrow && <span aria-hidden="true">{arrow} </span>}
        {text}
      </Box>
    </Box>
  );
};

export const LiftCard = ({ exercise, weeks }) => {
  const hasData = hasExerciseData(exercise);
  const trend = getLiftTrend(exercise);
  return (
    <Card component="li" sx={{ listStyle: 'none' }}>
      <Typography variant="h3" sx={{ fontSize: 17, fontWeight: 700 }}>
        {exercise.label}
      </Typography>
      {!hasData ? (
        <Typography sx={{ mt: 1, opacity: 0.75, fontSize: 14 }}>
          Not enough data yet. Log a few sets on this lift and your rating will appear here.
        </Typography>
      ) : (
        <>
          <Typography sx={{ mt: 1, fontSize: 28, fontWeight: 800 }}>
            {formatOrm(exercise.currentOrm)}
            <Box component="span" sx={{ fontSize: 13, fontWeight: 400, ml: 1, opacity: 0.7 }}>
              est. 1RM
            </Box>
          </Typography>
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: 13, opacity: 0.8, mb: 0.5 }}>
              {isNum(exercise.percentOfBest)
                ? `${formatNumber(
                    exercise.percentOfBest,
                    0
                  )}% of your best this window (${formatOrm(exercise.bestOrm)})`
                : 'Best not available yet'}
            </Typography>
            {isNum(exercise.percentOfBest) && (
              <MeterBar
                value={exercise.percentOfBest}
                label={`${exercise.label} current estimated 1RM as percent of your best in this window`}
              />
            )}
          </Box>
          <Box sx={{ mt: 1.5, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Change label="30 days" value={exercise.change30dPct} />
            <Change label="90 days" value={exercise.change90dPct} />
          </Box>
          <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <StatusChip label={TREND_LABELS[trend]} tone={TREND_TONE[trend]} />
            <StatusChip label={CONFIDENCE_LABELS[exercise.confidence]} />
          </Box>
          <Box sx={{ mt: 1 }}>
            <Sparkline points={exercise.sparkline} label={exercise.label} weeks={weeks} />
          </Box>
        </>
      )}
    </Card>
  );
};

const LiftCards = ({ exercises, weeks }) => (
  <Section title="Lift ratings" id="perf-lifts">
    <Box
      component="ul"
      sx={{
        p: 0,
        m: 0,
        display: 'grid',
        gap: 2,
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          tl: 'repeat(2, minmax(0, 1fr))',
          lg: 'repeat(4, minmax(0, 1fr))',
        },
      }}
    >
      {(exercises || []).map((exercise) => (
        <LiftCard key={exercise.exerciseName} exercise={exercise} weeks={weeks} />
      ))}
    </Box>
  </Section>
);

export default LiftCards;
